import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Logo from "../Logo";

const ICON_DOT = (
    <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-60 mr-3" />
);

function SidebarLink({ to, label, end, testId }) {
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={testId}
            className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                    isActive
                        ? "bg-avx-blue/10 text-avx-blue border-l-2 border-avx-blue"
                        : "text-avx-text/70 hover:text-avx-blue hover:bg-white/[0.02] border-l-2 border-transparent"
                }`
            }
        >
            {ICON_DOT}
            <span>{label}</span>
        </NavLink>
    );
}

export default function DashboardLayout({ children, role }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const isAdmin = role === "admin";
    const base = isAdmin ? "/admin" : "/dashboard";

    return (
        <div className="min-h-screen flex flex-col bg-avx-bg">
            <header className="sticky top-0 z-40 border-b border-avx-line bg-avx-bg/90 backdrop-blur-xl">
                <div className="px-6 h-16 flex items-center justify-between">
                    <Logo to={isAdmin ? "/admin" : "/dashboard"} />
                    <div className="flex items-center gap-3">
                        <NavLink to="/" className="text-xs font-mono uppercase tracking-[0.18em] text-avx-muted hover:text-avx-blue" data-testid="goto-public">
                            Public site
                        </NavLink>
                        <div className="hidden sm:flex flex-col items-end leading-tight">
                            <span className="text-sm text-avx-text">{user?.name}</span>
                            <span className="label-mono text-[9px]">{user?.role}</span>
                        </div>
                        <div className="w-9 h-9 rounded-sm bg-avx-elevated border border-avx-line flex items-center justify-center font-heading font-bold">
                            {user?.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <button
                            onClick={async () => { await logout(); navigate("/login"); }}
                            data-testid="dashboard-logout-btn"
                            className="text-xs font-mono uppercase tracking-[0.18em] text-avx-muted hover:text-avx-danger px-2"
                        >
                            Log out
                        </button>
                    </div>
                </div>
            </header>

            <div className="flex flex-1 overflow-hidden">
                <aside className="w-60 shrink-0 border-r border-avx-line bg-avx-surface/50 hidden md:block">
                    <div className="p-4 space-y-1">
                        <div className="label-mono px-3 mb-2">{isAdmin ? "Admin panel" : "Client panel"}</div>
                        {isAdmin ? (
                            <>
                                <SidebarLink to={base} end label="Dashboard" testId="sb-admin-dash" />
                                <SidebarLink to={`${base}/users`} label="Users" testId="sb-admin-users" />
                                <SidebarLink to={`${base}/products`} label="Products" testId="sb-admin-products" />
                                <SidebarLink to={`${base}/categories`} label="Categorías" testId="sb-admin-categories" />
                                <SidebarLink to={`${base}/reviews`} label="Reseñas" testId="sb-admin-reviews" />
                                <SidebarLink to={`${base}/installers`} label="Installers" testId="sb-admin-installers" />
                                <SidebarLink to={`${base}/purchases`} label="Purchases" testId="sb-admin-purchases" />
                                <SidebarLink to={`${base}/assign`} label="Assign purchase" testId="sb-admin-assign" />
                                <SidebarLink to={`${base}/licenses`} label="Licenses" testId="sb-admin-licenses" />
                                <SidebarLink to={`${base}/usb-keys`} label="Llaves USB" testId="sb-admin-usb-keys" />
                                <SidebarLink to={`${base}/serials`} label="Serials" testId="sb-admin-serials" />
                                <SidebarLink to={`${base}/special-licenses`} label="Special licenses" testId="sb-admin-special-licenses" />
                                <SidebarLink to={`${base}/bundles`} label="Bundles" testId="sb-admin-bundles" />
                                <SidebarLink to={`${base}/coupons`} label="Coupons" testId="sb-admin-coupons" />
                                <SidebarLink to={`${base}/campaign`} label="CyberDay" testId="sb-admin-campaign" />
                                <SidebarLink to={`${base}/artists`} label="Artists" testId="sb-admin-artists" />
                                <SidebarLink to={`${base}/assign-artist`} label="Assign to artist" testId="sb-admin-assign-artist" />
                                <SidebarLink to={`${base}/desktop`} label="AVX Central" testId="sb-admin-desktop" />
                                <SidebarLink to={`${base}/downloads`} label="Downloads" testId="sb-admin-downloads" />
                                <SidebarLink to={`${base}/support`} label="Support" testId="sb-admin-support" />
                            </>
                        ) : (
                            <>
                                <SidebarLink to={base} end label="Overview" testId="sb-client-dash" />
                                <SidebarLink to={`${base}/purchases`} label="My Purchases" testId="sb-client-purchases" />
                                <SidebarLink to={`${base}/plugins`} label="My Plugins" testId="sb-client-plugins" />
                                <SidebarLink to={`${base}/redeem`} label="Redeem serial" testId="sb-client-redeem" />
                                <SidebarLink to={`${base}/licenses`} label="My Licenses" testId="sb-client-licenses" />
                                <SidebarLink to={`${base}/subscriptions`} label="My Subscriptions" testId="sb-client-subscriptions" />
                                <SidebarLink to={`${base}/downloads`} label="Downloads" testId="sb-client-downloads" />
                                <SidebarLink to={`${base}/support`} label="Support" testId="sb-client-support" />
                            </>
                        )}
                    </div>
                </aside>
                <main className="flex-1 overflow-y-auto">
                    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto">{children}</div>
                </main>
            </div>
        </div>
    );
}
